import { useState } from 'react';
import { X, Lock, Tag } from 'lucide-react';
import { toast } from 'sonner';
import { seguridadApi } from '../lib/api';

// 2026-10-22 (pedido del dueño): cambiar el precio de un producto al vender, o autorizar un
// descuento. Si el producto tiene precio en el catálogo, el cambio pide clave de
// administrador y motivo (el servidor lo vuelve a exigir al guardar). Si el producto está en
// S/ 0, se pone el precio sin clave y queda guardado en el catálogo.
interface Props {
  nombre: string;
  precioLista: number;
  precioActual: number;
  soloAutorizar?: boolean;
  onAplicar: (precio: number, autorizacion: { clave: string; motivo: string } | null) => void;
  onClose: () => void;
}

export default function CambiarPrecioModal({ nombre, precioLista, precioActual, soloAutorizar, onAplicar, onClose }: Props) {
  const [precio, setPrecio] = useState(soloAutorizar ? '' : String(precioActual || ''));
  const [motivo, setMotivo] = useState('');
  const [clave, setClave] = useState('');
  const [verificando, setVerificando] = useState(false);

  const nuevo = parseFloat(precio);
  const sinPrecioCatalogo = !soloAutorizar && precioLista <= 0;
  const pideClave = soloAutorizar || (!sinPrecioCatalogo && !isNaN(nuevo) && Math.abs(nuevo - precioLista) > 0.005);

  const aplicar = async () => {
    if (!soloAutorizar && (isNaN(nuevo) || nuevo <= 0)) return toast.error('Escribe un precio mayor a 0');
    if (!pideClave) return onAplicar(Math.round(nuevo * 100) / 100, null);
    if (!motivo.trim()) return toast.error('Escribe el motivo');
    if (!clave) return toast.error('Escribe la clave de administrador');
    setVerificando(true);
    try {
      await seguridadApi.verificarClave(clave);
      onAplicar(soloAutorizar ? 0 : Math.round(nuevo * 100) / 100, { clave, motivo: motivo.trim() });
    } catch (e: any) {
      toast.error(e.message || 'Clave de administrador incorrecta');
    } finally {
      setVerificando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-lg w-full max-w-sm">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-semibold flex items-center gap-2">
            {soloAutorizar ? <Lock className="w-5 h-5 text-amber-600" /> : <Tag className="w-5 h-5 text-blue-600" />}
            {soloAutorizar ? 'Autorizar descuento' : 'Cambiar precio'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-sm font-medium">{nombre}</p>
          {!soloAutorizar && (
            <>
              <p className="text-xs text-gray-600">
                {sinPrecioCatalogo
                  ? 'Este producto no tiene precio en el catálogo: el precio que pongas quedará guardado para las próximas ventas.'
                  : `Precio de lista: S/ ${precioLista.toFixed(2)}`}
              </p>
              <div>
                <label className="block text-xs font-medium mb-1">Precio de venta (S/ por unidad)</label>
                <input type="number" step="0.01" min="0.01" value={precio} autoFocus
                       onChange={(e) => setPrecio(e.target.value)} className="w-full border rounded-lg px-3 py-2" />
              </div>
            </>
          )}
          {pideClave && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
              <p className="text-xs text-amber-900 font-medium">Se necesita la clave de administrador. Queda registrado en Auditoría.</p>
              <input type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={200}
                     placeholder="Motivo (ej.: cliente frecuente, precio acordado)" className="w-full border rounded-lg px-3 py-2 text-sm" />
              <input type="password" value={clave} onChange={(e) => setClave(e.target.value)} autoComplete="off"
                     placeholder="Clave de administrador" className="w-full border rounded-lg px-3 py-2 text-sm"
                     onKeyDown={(e) => { if (e.key === 'Enter') aplicar(); }} />
            </div>
          )}
          <button onClick={aplicar} disabled={verificando}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg disabled:opacity-50">
            {verificando ? 'Verificando...' : (soloAutorizar ? 'Autorizar' : 'Aplicar precio')}
          </button>
        </div>
      </div>
    </div>
  );
}
